import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api/rawFetch'

// Interfaces declared locally rather than pulled from generated.ts — the
// generator only speaks to a live backend, and regenerating against the current
// app drags in thousands of lines of unrelated drift. Same escape hatch as
// useProductRequests.ts / useEvents.ts. Mirrors backend/schemas/stocktake.py.

export type StocktakeStatus = 'counting' | 'review' | 'applied' | 'cancelled'

/** Open = still someone's job; archived = a record of what happened. Drives the
 *  two tabs on the list page, which filters client-side off one fetch. */
export const OPEN_STOCKTAKE_STATUSES: StocktakeStatus[] = ['counting', 'review']

export interface StocktakeLocation {
  location_id: number
  name: string
}

export interface StocktakeSessionSummary {
  session_id: number
  name: string
  status: string
  location_id: number | null
  location_name: string
  filter_vendor: string | null
  /** DISPLAY CAPTION ONLY — comma-joined and truncated at 200 chars. Never
   *  split it back apart; use `product_types` for the real set. */
  filter_product_type: string | null
  /** The picked types, canonical. `[]` = every type this vendor sells, which
   *  is also what sessions predating this field report. */
  product_types: string[]
  created_by: string | null
  created_at: string | null
  applied_by: string | null
  applied_at: string | null
  /** The stock_adjustments record this session produced, once applied. */
  adjustment_id: number | null
  /** Lines that have BEEN counted (an explicit 0 counts) — "42 of 110". */
  counted_lines: number
  total_lines: number
  /** SUM(counted − baseline) over the COUNTED lines only. */
  net_variance: number
}

export interface StocktakeLine {
  line_id: number
  variant_id: number
  product_title: string
  variant_title: string
  sku: string
  barcode: string
  /** Stocky's "Available": live Shopify on-hand captured at creation. Frozen. */
  baseline_on_hand: number | null
  /** Shopify units committed to unfulfilled orders (holds/exchanges) at
   *  creation. Informational: these may be boxed off the sales floor, which is
   *  why they show up as Missed with nobody having scanned them. */
  baseline_committed: number | null
  /** Stocky's "Scanned". null = NEVER counted (the apply leaves this variant
   *  alone); 0 = counted and found empty (the apply drives Shopify to 0). */
  counted_qty: number | null
  counted_by: string | null
  last_counted_at: string | null
  /** Stocky's "Adjustment" column: counted − baseline. */
  variance: number | null
  applied_delta: number | null
  apply_status: string | null
  apply_error: string | null
}

export interface StocktakeDetail {
  session_id: number
  name: string
  status: string
  location_id: number | null
  location_name: string
  filter_vendor: string | null
  /** DISPLAY CAPTION ONLY — see StocktakeSessionSummary.filter_product_type. */
  filter_product_type: string | null
  /** The picked types, canonical. `[]` = every type this vendor sells. */
  product_types: string[]
  notes: string | null
  created_by: string | null
  created_at: string | null
  applied_by: string | null
  applied_at: string | null
  apply_error: string | null
  adjustment_id: number | null
  lines: StocktakeLine[]
}

export interface StocktakeFilterOptions {
  vendors: string[]
  /** Narrowed to the requested vendor when one is passed. */
  product_types: string[]
  locations: StocktakeLocation[]
}

export interface StocktakeCreatePayload {
  name: string
  /** A stocktake is always one vendor at one location — both required. */
  vendor: string
  location_id: number
  /** Narrow the rack to these product types. Omitted = every type the vendor
   *  sells at this location. */
  product_types?: string[]
  notes?: string
}

/** Edit of an OPEN session. Absent field = leave alone.
 *
 *  `name`/`notes` are pure bookkeeping and editable in any status. The scope
 *  fields below re-derive the line set, so they need a LIVE Shopify read for
 *  the added lines' Available baseline (503 without credentials) and the server
 *  refuses them (409) once they would destroy work — see `discard_counted`. */
export interface StocktakeUpdatePayload {
  name?: string
  notes?: string
  /** Scope. Moving the vendor re-walks the rack; refused (409) once any line
   *  has been counted. */
  vendor?: string
  /** Scope. `[]` = every type this vendor sells. Narrowing it deletes the
   *  dropped types' lines, which is a 409 when any of them were counted. */
  product_types?: string[]
  /** Scope. Same counted-lines rule as `vendor` — the baseline belongs to a
   *  location. */
  location_id?: number
  /** Acknowledgement, not a scope field: resend the IDENTICAL payload with this
   *  set to let the server throw away the counts on the lines it drops. */
  discard_counted?: boolean
}

/** Another OPEN session at this location that has already counted the variant
 *  just scanned. Advisory only — the scan still counted. */
export interface StocktakeAlsoCountedIn {
  session_id: number
  name: string
  counted_qty: number
}

export interface StocktakeScanResponse {
  matched: boolean
  /** 'unmatched' (nothing in THIS stocktake carries the code) or 'ambiguous'. */
  reason: 'unmatched' | 'ambiguous' | null
  line: StocktakeLine | null
  /** Empty normally. Non-empty = two open counts are about to fight over the
   *  same stock, which the UI raises as a non-blocking warning. */
  also_counted_in?: StocktakeAlsoCountedIn[]
}

/** One physical scan, misses included (variant_id null = "Barcode not found"). */
export interface StocktakeScanLogEntry {
  scan_id: number
  barcode: string
  variant_id: number | null
  product_title: string
  variant_title: string
  sku: string
  qty_scanned: number
  scanned_by: string | null
  scan_timestamp: string | null
}

/** Live discrepancy preview for one line. `planned_delta` is counted − live
 *  on-hand, so it is NOT the cached `variance` on StocktakeLine. */
export interface StocktakeReviewLine {
  line_id: number
  variant_id: number
  product_title: string
  variant_title: string
  sku: string
  /** "New Available Quantity". */
  counted_qty: number | null
  live_available: number | null
  /** "Old Available Quantity" — what the delta is computed against. */
  live_on_hand: number | null
  planned_delta: number | null
  apply_status_preview: 'ok' | 'skipped_zero' | 'skipped_uncounted'
}

export interface StocktakeReview {
  session_id: number
  location_id: number | null
  location_name: string
  /** ONLY the lines with a nonzero adjustment — the server filters. */
  lines: StocktakeReviewLine[]
  total_lines: number
  /** Lines nobody ever counted. NOT in `lines`, and never adjusted. */
  uncounted_lines: number
  variants_adjusted: number
  units_added: number
  units_removed: number
  total_planned_delta: number
}

export interface StocktakeApplyResult {
  ok: boolean
  adjusted: number
  skipped_zero: number
  skipped_uncounted: number
  /** The stock_adjustments record just filed; null when the push failed. */
  adjustment_id: number | null
  error: string | null
}

/** Progress for the counting header. A line counts as counted once it has ANY
 *  count on it, including a deliberate 0 ("this peg really is empty") — that
 *  is real progress and the only thing that puts the variant in the apply. */
export function countedOf(lines: StocktakeLine[]): { counted: number; total: number } {
  return {
    counted: lines.filter((l) => l.counted_qty !== null).length,
    total: lines.length,
  }
}

/** The Missed tab: never counted. These are NOT adjusted at apply — Shopify
 *  keeps whatever it has — unless someone marks them 0 on purpose. */
export function missedOf(lines: StocktakeLine[]): StocktakeLine[] {
  return lines.filter((l) => l.counted_qty === null)
}

/** The Scan tab: what has actually been counted, newest scan first, the way
 *  Stocky's scan sheet grows from empty as items come off the rack. */
export function scannedOf(lines: StocktakeLine[]): StocktakeLine[] {
  return lines
    .filter((l) => l.counted_qty !== null)
    .sort((a, b) => (b.last_counted_at ?? '').localeCompare(a.last_counted_at ?? ''))
}

export function useStocktakeList(status?: string) {
  return useQuery({
    queryKey: ['stocktakes', status ?? ''] as const,
    queryFn: () =>
      apiFetch<StocktakeSessionSummary[]>('GET', '/api/stocktakes', {
        query: { status },
      }),
    staleTime: 30_000,
  })
}

export function useStocktake(sessionId: string | null) {
  return useQuery({
    queryKey: ['stocktake', sessionId] as const,
    queryFn: () => apiFetch<StocktakeDetail>('GET', `/api/stocktakes/${sessionId}`),
    enabled: !!sessionId,
  })
}

/** Create-modal dropdowns. Passing a vendor narrows the product-type list to
 *  the types that vendor actually sells, so it's part of the cache key. */
export function useStocktakeFilterOptions(vendor?: string) {
  return useQuery({
    queryKey: ['stocktake-filter-options', vendor ?? ''] as const,
    queryFn: () =>
      apiFetch<StocktakeFilterOptions>('GET', '/api/stocktakes/filter-options', {
        query: { vendor: vendor || undefined },
      }),
    staleTime: 5 * 60_000,
  })
}

/** The Log tab. Kept out of the ['stocktake', id] detail key so a scan can
 *  refresh the count sheet without dragging the whole log along. */
export function useStocktakeScanLog(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['stocktake-scans', sessionId] as const,
    queryFn: () =>
      apiFetch<StocktakeScanLogEntry[]>('GET', `/api/stocktakes/${sessionId}/scans`),
    enabled: enabled && !!sessionId,
    staleTime: 0,
  })
}

export function useCreateStocktake() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StocktakeCreatePayload) =>
      apiFetch<StocktakeDetail>('POST', '/api/stocktakes', { body: payload }),
    onSuccess: (detail) => {
      qc.setQueryData(['stocktake', String(detail.session_id)], detail)
      qc.invalidateQueries({ queryKey: ['stocktakes'] })
    },
  })
}

/** Manual count entry from the Search tab. `set` writes the typed quantity;
 *  `increment` adds to it. 404 = the variant is not part of this stocktake. */
export function useUpsertLine(sessionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { variant_id: number; qty: number; mode?: 'set' | 'increment' }) =>
      apiFetch<StocktakeDetail>('POST', `/api/stocktakes/${sessionId}/lines`, {
        body: {
          variant_id: vars.variant_id,
          qty: vars.qty,
          mode: vars.mode ?? 'set',
        },
      }),
    onSuccess: (detail) => {
      qc.setQueryData(['stocktake', sessionId], detail)
      qc.invalidateQueries({ queryKey: ['stocktakes'] })
    },
  })
}

/** Live variance preview. Deliberately NOT under the ['stocktake', id] detail
 *  key: this endpoint calls out to Shopify, so every count edit invalidating the
 *  detail must not drag a live inventory read along with it. staleTime 0 because
 *  the live numbers move under us — the operator refetches on purpose. */
export function useStocktakeReview(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['stocktake-review', sessionId] as const,
    queryFn: () =>
      apiFetch<StocktakeReview>('GET', `/api/stocktakes/${sessionId}/review`),
    enabled: enabled && !!sessionId,
    staleTime: 0,
    retry: false,
  })
}

/** Push the count to Shopify. Open to any user — whoever walked the rack is who
 *  closes the count.
 *
 *  NB: a Shopify failure comes back as HTTP 200 with ok=false — callers must
 *  check `ok`, not just the absence of a thrown error. Either way the detail is
 *  invalidated: success flips the session to 'applied' and stamps
 *  adjustment_id, failure writes apply_error and files no adjustment. */
export function useApplyStocktake(sessionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<StocktakeApplyResult>('POST', `/api/stocktakes/${sessionId}/apply`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stocktake', sessionId] })
      qc.invalidateQueries({ queryKey: ['stocktakes'] })
      qc.invalidateQueries({ queryKey: ['stocktake-review', sessionId] })
    },
  })
}

/** Rename / re-note / re-scope a session. Any user: the label on a count is
 *  bookkeeping, not part of the audit trail. Only the changed fields are sent —
 *  an empty `notes` string is a real value and clears the note.
 *
 *  A SCOPE change (vendor / product_types / location_id) is a different animal:
 *  it re-derives the lines against a live Shopify read, so it 503s in offline
 *  mode and 409s when it would delete counted work or touch an archived
 *  session. Callers must handle both — see StocktakeSession's edit dialog.
 *
 *  The returned detail carries the NEW line set, so the review preview computed
 *  against the old one is thrown away too. */
export function useUpdateStocktake(sessionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StocktakeUpdatePayload) =>
      apiFetch<StocktakeDetail>('PATCH', `/api/stocktakes/${sessionId}`, {
        body: payload,
      }),
    onSuccess: (detail) => {
      qc.setQueryData(['stocktake', sessionId], detail)
      qc.invalidateQueries({ queryKey: ['stocktakes'] })
      qc.invalidateQueries({ queryKey: ['stocktake-review', sessionId] })
    },
  })
}

/** Erase a session and its scan log outright. Admin only, and the server
 *  refuses (409) for an 'applied' one — that session IS the audit record behind
 *  a real Shopify adjustment. Cancelling keeps the record; this does not. */
export function useDeleteStocktake(sessionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>('DELETE', `/api/stocktakes/${sessionId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stocktakes'] })
    },
  })
}

/** counting ↔ review (anyone) and counting/review → cancelled (admin only).
 *  'applied' is reached through /apply, never here. */
export function useChangeStatus(sessionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (status: StocktakeStatus) =>
      apiFetch<StocktakeDetail>('POST', `/api/stocktakes/${sessionId}/status`, {
        body: { status },
      }),
    onSuccess: (detail) => {
      qc.setQueryData(['stocktake', sessionId], detail)
      qc.invalidateQueries({ queryKey: ['stocktakes'] })
    },
  })
}
